// RegisterLayout — stránka "/register": samoregistrace nového uživatele.
// Odešle POST /api/auth/register (žádné heslo). Účet vzniká jako 'pending' —
// admin ho musí schválit, pak uživateli přijde e-mail s odkazem pro nastavení hesla.
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authApi } from '../api/auth'
import { useTrackView } from '../hooks/useTrackProduct'

const EMPTY = {
  username: '', email: '', first_name: '', last_name: '', display_name: '',
  request_message: '',
}

export function RegisterLayout() {
  const navigate = useNavigate()
  // Sledování přístupů (etapa K2) — registrace je systémová část bez uzlu v registru.
  useTrackView('auth', 'register')
  const [form, setForm]       = useState(EMPTY)
  const [error, setError]     = useState(null)      // obecná chyba
  const [fieldErr, setFieldErr] = useState({})      // { username?, email?, ... }
  const [loading, setLoading] = useState(false)
  const [done, setDone]       = useState(false)

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }))

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setFieldErr({})
    setLoading(true)
    try {
      await authApi.register(form)
      setDone(true)
    } catch (err) {
      if (err.field) setFieldErr({ [err.field]: err.message })
      else setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 w-full max-w-md flex flex-col gap-5">
          <h1 className="text-xl font-bold text-gray-800">Registrace odeslána</h1>
          <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            Účet byl vytvořen a čeká na schválení administrátorem. Až bude schválen,
            dostanete e-mail s odkazem pro nastavení hesla.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors self-end"
          >
            Zpět na hlavní stránku
          </button>
        </div>
      </div>
    )
  }

  const inputCls = 'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400'

  function field(label, key, { type = 'text', required = false } = {}) {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-sm text-gray-600 font-medium">{label}{required && ' *'}</label>
        <input type={type} value={form[key]} onChange={set(key)} required={required}
          autoComplete="off" className={inputCls} />
        {fieldErr[key] && <span className="text-xs text-red-600">{fieldErr[key]}</span>}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-8">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 w-full max-w-md flex flex-col gap-5">
        <h1 className="text-xl font-bold text-gray-800">Registrace nového účtu</h1>
        <p className="text-sm text-gray-500">
          Účet po registraci schvaluje administrátor. Heslo si nastavíte přes odkaz,
          který vám přijde e-mailem po schválení.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {field('Uživatelské jméno', 'username', { required: true })}
          {field('E-mail', 'email', { type: 'email', required: true })}
          <div className="grid grid-cols-2 gap-3">
            {field('Jméno', 'first_name')}
            {field('Příjmení', 'last_name')}
          </div>
          {field('Zobrazované jméno', 'display_name')}

          <div className="flex flex-col gap-1">
            <label className="text-sm text-gray-600 font-medium">Vzkaz pro administrátora</label>
            <textarea
              value={form.request_message}
              onChange={set('request_message')}
              rows={3}
              maxLength={2000}
              placeholder="Napište, proč účet chcete a jaká oprávnění potřebujete. Administrátor to uvidí při schvalování."
              className={inputCls}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}

          <div className="flex gap-3 justify-end pt-1">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Odesílám…' : 'Zaregistrovat se'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
